/**
 * Armes en vue première personne — 4 armes Q3 modélisées de façon procédurale
 * et attachées au rig de la caméra, avec mains gantées, ressorts de recul,
 * inertie de visée (sway), bob de marche et écrans de munitions en canvas.
 *
 * Aucune texture fichier : matériaux PBR de `render/materials` + petits canvas.
 * Aucune PointLight dynamique : le pool du moteur FX s'en charge côté jeu.
 */

import * as THREE from 'three';
import { WeaponId, WEAPONS } from './weapons';
import { SOLID } from '../render/materials';

type Kick = { z: number; pitch: number; yaw: number; roll: number; punch: number };

/** Amplitudes de recul par arme (position + rotation du rig, punch caméra). */
const KICKS: Record<WeaponId, Kick> = {
  lightning: { z: 0.008, pitch: 0.006, yaw: 0.0015, roll: 0.001, punch: 0.0016 },
  rocket:    { z: 0.050, pitch: 0.055, yaw: 0.004,  roll: 0.006, punch: 0.022 },
  rail:      { z: 0.035, pitch: 0.042, yaw: 0.003,  roll: 0.004, punch: 0.018 },
  shotgun:   { z: 0.060, pitch: 0.060, yaw: 0.0035, roll: 0.010, punch: 0.024 },
};

interface WeaponRig {
  group: THREE.Group;
  muzzle: THREE.Object3D;
  /** animations continues (t global, dt) */
  anim: Array<(t: number, dt: number) => void>;
  /** déclenché au tir */
  fire: Array<() => void>;
  /** écran de munitions (canvas) */
  screen?: { ctx: CanvasRenderingContext2D; tex: THREE.CanvasTexture; label: string; color: string };
  ammo: number;
  /** matériaux teintés par l'accent de la légende (bandes des gants) */
  accentMats: THREE.MeshStandardMaterial[];
}

export class ViewWeapon {
  private rig = new THREE.Group();
  private rigs = new Map<WeaponId, WeaponRig>();
  private current: WeaponId = 'lightning';

  // ressorts
  private kickZ = 0; private kickPitch = 0; private kickYaw = 0; private kickRoll = 0;
  private punchP = 0; private punchY = 0;
  private emitP = 0; private emitY = 0;
  private swayX = 0; private swayY = 0;   // inertie de visée (suit la souris)
  private lookAX = 0; private lookAY = 0; // accumulateur de deltas souris
  private bobT = 0; private idleT = 0;
  private landDip = 0; private wasGround = true; private switchT = 0;
  private accent = 0xf7931a;

  // base rapprochée + rig réduit : le canon du RG saillait à ~1,93 m devant la
  // caméra et traversait les murs en strafe. Ramené à ~1,65 m.
  private base = new THREE.Vector3(0.30, -0.30, -0.38);
  private isTouch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

  // matériaux partagés (une seule instance pour les 4 armes)
  private M = {
    hull: SOLID.gunmetal(),
    steel: SOLID.steel(),
    brushed: SOLID.brushed(),
    carbon: SOLID.carbon(),
    rubber: SOLID.rubber(),
    orange: SOLID.orange(),
    brass: new THREE.MeshStandardMaterial({ color: 0xc9a227, roughness: 0.28, metalness: 1.0, envMapIntensity: 1.4 }),
    wood: new THREE.MeshStandardMaterial({ color: 0x3a2416, roughness: 0.75, metalness: 0.05 }),
    glassRed: new THREE.MeshStandardMaterial({ color: 0x120404, roughness: 0.15, metalness: 0.2, emissive: new THREE.Color(0xff2200), emissiveIntensity: 0.6 }),
  };

  constructor(camera: THREE.Camera, accent = 0xf7931a) {
    this.accent = accent;
    camera.add(this.rig);
    if (this.isTouch) this.base.set(0.27, -0.28, -0.34);
    this.rig.position.copy(this.base);
    this.rig.scale.setScalar(this.isTouch ? 1.10 : 1.20);

    for (const id of Object.keys(WEAPONS) as WeaponId[]) {
      const w = this.build(id);
      w.group.visible = id === 'lightning';
      this.rig.add(w.group);
      this.rigs.set(id, w);
    }
    this.setAccent(accent);
  }

  // ---------------------------------------------------------------- outils de construction

  private glowMat(c: number, i = 3): THREE.MeshStandardMaterial {
    return SOLID.glow(c, i);
  }

  /** Petit écran LCD (canvas) affichant les munitions. */
  private makeScreen(label: string, color: string): { mesh: THREE.Mesh; screen: WeaponRig['screen'] } {
    const c = document.createElement('canvas');
    c.width = 128; c.height = 64;
    const ctx = c.getContext('2d')!;
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    const mat = new THREE.MeshBasicMaterial({ map: tex, toneMapped: false });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(0.085, 0.043), mat);
    return { mesh, screen: { ctx, tex, label, color } };
  }

  private drawScreen(s: NonNullable<WeaponRig['screen']>, ammo: number): void {
    const { ctx } = s;
    ctx.fillStyle = '#04070a';
    ctx.fillRect(0, 0, 128, 64);
    ctx.strokeStyle = 'rgba(255,255,255,.08)';
    for (let y = 2; y < 64; y += 4) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(128, y); ctx.stroke(); }
    ctx.fillStyle = s.color;
    ctx.font = 'bold 12px ui-monospace, monospace';
    ctx.textAlign = 'left';
    ctx.fillText(s.label, 6, 16);
    ctx.font = 'bold 34px ui-monospace, monospace';
    ctx.textAlign = 'right';
    ctx.shadowColor = s.color; ctx.shadowBlur = 10;
    ctx.fillText(String(Math.max(0, Math.min(999, Math.round(ammo)))), 122, 52);
    ctx.shadowBlur = 0;
    s.tex.needsUpdate = true;
  }

  /**
   * Main gantée : poignet, paume, 4 doigts (2 phalanges) + pouce.
   * `side` = -1 (gauche) / +1 (droite). Le groupe est orienté pour saisir
   * un objet situé devant, l'axe des doigts pointant vers -Y local.
   */
  private makeHand(side: number, glove: THREE.MeshStandardMaterial, band: THREE.MeshStandardMaterial): THREE.Group {
    const h = new THREE.Group();
    const add = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0) => {
      const m = new THREE.Mesh(geo, mat); m.position.set(x, y, z); m.rotation.set(rx, ry, rz); h.add(m); return m;
    };
    // avant-bras + poignet
    add(new THREE.CapsuleGeometry(0.036, 0.13, 3, 8), glove, 0, 0.13, 0.04, 0.35);
    add(new THREE.CylinderGeometry(0.041, 0.037, 0.028, 10), band, 0, 0.055, 0.015);
    // paume
    add(new THREE.BoxGeometry(0.052, 0.075, 0.075), glove, 0, 0.005, 0);
    add(new THREE.BoxGeometry(0.054, 0.02, 0.058), band, 0, 0.03, 0.004); // sangle dorsale
    // doigts : 4 × 2 phalanges refermées autour de l'axe Z
    for (let i = 0; i < 4; i++) {
      const x = (-1.5 + i) * 0.017;
      const p1 = add(new THREE.CapsuleGeometry(0.0085, 0.032, 3, 6), glove, x, -0.042, -0.026, 1.15);
      p1.rotation.x = 1.15;
      const p2 = add(new THREE.CapsuleGeometry(0.008, 0.026, 3, 6), glove, x, -0.052, -0.052 + i * 0.001, 2.2);
      p2.rotation.x = 2.2;
      add(new THREE.BoxGeometry(0.014, 0.008, 0.012), band, x, -0.05, -0.04, 1.15);
    }
    // pouce
    const th = add(new THREE.CapsuleGeometry(0.011, 0.036, 3, 6), glove, side * 0.03, -0.018, -0.03, 0.9, 0, side * 0.7);
    th.rotation.set(0.9, 0, side * 0.7);
    return h;
  }

  private build(id: WeaponId): WeaponRig {
    const g = new THREE.Group();
    const M = this.M;
    const anim: Array<(t: number, dt: number) => void> = [];
    const fire: Array<() => void> = [];
    const accentMats: THREE.MeshStandardMaterial[] = [];
    const glove = new THREE.MeshStandardMaterial({ color: 0x101218, roughness: 0.62, metalness: 0.28 });
    const band = new THREE.MeshStandardMaterial({ color: this.accent, roughness: 0.45, metalness: 0.5, emissive: new THREE.Color(this.accent), emissiveIntensity: 0.25 });
    accentMats.push(band);

    const add = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0) => {
      const m = new THREE.Mesh(geo, mat); m.position.set(x, y, z); m.rotation.set(rx, ry, rz);
      m.castShadow = false; m.receiveShadow = false; g.add(m); return m;
    };
    const muzzle = new THREE.Object3D();
    let screen: WeaponRig['screen'];
    // Position du grip et du garde-main — les mains s'y accrochent.
    let gripAt = new THREE.Vector3(0, -0.15, 0.14);
    let foreAt = new THREE.Vector3(0, -0.10, -0.34);

    switch (id) {
      // ------------------------------------------------------------ LIGHTNING GUN
      case 'lightning': {
        // récepteur chanfreiné
        add(new THREE.BoxGeometry(0.115, 0.14, 0.40), M.hull, 0, 0, 0.06);
        add(new THREE.BoxGeometry(0.125, 0.055, 0.34), M.carbon, 0, 0.055, 0.05);
        add(new THREE.BoxGeometry(0.13, 0.03, 0.10), M.brushed, 0, 0.085, 0.16);   // rail supérieur
        add(new THREE.BoxGeometry(0.02, 0.045, 0.02), M.brushed, 0, 0.105, -0.02); // guidon
        // guide-câbles tubulaires latéraux
        for (const s of [-1, 1]) {
          add(new THREE.CylinderGeometry(0.014, 0.014, 0.52, 8), M.brushed, s * 0.072, 0.015, -0.18, Math.PI / 2);
          add(new THREE.TorusGeometry(0.018, 0.005, 6, 10), M.steel, s * 0.072, 0.015, -0.05, 0, Math.PI / 2);
          add(new THREE.TorusGeometry(0.018, 0.005, 6, 10), M.steel, s * 0.072, 0.015, -0.32, 0, Math.PI / 2);
        }
        // cellules d'énergie
        const cells: THREE.Mesh[] = [];
        for (let i = 0; i < 3; i++) {
          const c = add(new THREE.CylinderGeometry(0.026, 0.026, 0.15, 10), this.glowMat(0x9be7ff, 1.6), -0.075 + i * 0.075, -0.075, 0.10, Math.PI / 2);
          cells.push(c);
          add(new THREE.CylinderGeometry(0.029, 0.029, 0.018, 10), M.brushed, -0.075 + i * 0.075, -0.075, 0.175, Math.PI / 2);
        }
        // canon central
        add(new THREE.CylinderGeometry(0.022, 0.028, 0.58, 12), M.steel, 0, 0.01, -0.44, Math.PI / 2);
        // 4 bobines toriques (2 émissives) contra-rotatives
        const coils: THREE.Mesh[] = [];
        for (let i = 0; i < 4; i++) {
          const emissive = i % 2 === 1;
          const c = add(new THREE.TorusGeometry(0.058 + i * 0.004, 0.012, 8, 20), emissive ? this.glowMat(0x9be7ff, 3.2) : M.brushed, 0, 0.01, -0.24 - i * 0.115);
          coils.push(c);
        }
        // 4 fourches d'émetteur convergentes
        const forkTips: THREE.Mesh[] = [];
        for (let k = 0; k < 4; k++) {
          const a = (k / 4) * Math.PI * 2 + Math.PI / 4;
          const cx = Math.cos(a) * 0.048, cy = 0.01 + Math.sin(a) * 0.048;
          add(new THREE.BoxGeometry(0.014, 0.014, 0.17), M.steel, cx * 1.1, 0.01 + (cy - 0.01) * 1.1, -0.76, 0, 0, a);
          const tip = add(new THREE.SphereGeometry(0.014, 8, 6), this.glowMat(0xffffff, 4), cx * 0.55, 0.01 + (cy - 0.01) * 0.55, -0.86);
          forkTips.push(tip);
        }
        // arcs électriques entre fourches (segments cachés, révélés au tir)
        const arcs: THREE.Mesh[] = [];
        for (let k = 0; k < 4; k++) {
          const a1 = (k / 4) * Math.PI * 2 + Math.PI / 4, a2 = ((k + 1) / 4) * Math.PI * 2 + Math.PI / 4;
          const p1 = new THREE.Vector3(Math.cos(a1) * 0.026, 0.01 + Math.sin(a1) * 0.026, -0.86);
          const p2 = new THREE.Vector3(Math.cos(a2) * 0.026, 0.01 + Math.sin(a2) * 0.026, -0.86);
          const mid = p1.clone().lerp(p2, 0.5);
          const len = p1.distanceTo(p2);
          const arc = add(new THREE.CylinderGeometry(0.0035, 0.0035, len, 4), this.glowMat(0xdffaff, 6), mid.x, mid.y, mid.z);
          arc.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), p2.clone().sub(p1).normalize());
          arc.visible = false;
          arcs.push(arc);
        }
        // grip / garde / détente
        add(new THREE.BoxGeometry(0.062, 0.20, 0.085), M.rubber, 0, -0.155, 0.155, 0.26);
        add(new THREE.BoxGeometry(0.05, 0.012, 0.02), M.rubber, 0, -0.245, 0.19, 0.26);
        add(new THREE.TorusGeometry(0.045, 0.008, 6, 12, Math.PI), M.brushed, 0, -0.085, 0.075, 0, Math.PI / 2, Math.PI);
        add(new THREE.BoxGeometry(0.012, 0.038, 0.012), M.steel, 0, -0.085, 0.075, 0.2);
        // écran latéral
        const sc = this.makeScreen('LG', '#9be7ff');
        sc.mesh.position.set(-0.062, 0.02, 0.06); sc.mesh.rotation.y = -Math.PI / 2;
        g.add(sc.mesh); screen = sc.screen;

        muzzle.position.set(0, 0.01, -0.90);
        gripAt.set(0, -0.155, 0.155);
        foreAt.set(0, -0.085, -0.30);

        let pulse = 0;
        fire.push(() => { pulse = 1; });
        anim.push((t, dt) => {
          pulse = Math.max(0, pulse - dt * 6);
          coils.forEach((c, i) => {
            c.rotation.z = t * (2.2 + i * 0.8) * (i % 2 ? 1 : -1);
            const s = 1 + Math.sin(t * 8 + i) * 0.025 + pulse * 0.09;
            c.scale.setScalar(s);
            const m = c.material as THREE.MeshStandardMaterial;
            if (m.emissive.r + m.emissive.g + m.emissive.b > 0) m.emissiveIntensity = 2.4 + Math.sin(t * 9 + i) * 0.5 + pulse * 5;
          });
          cells.forEach((c, i) => { (c.material as THREE.MeshStandardMaterial).emissiveIntensity = 1.2 + Math.sin(t * 4 + i * 2) * 0.4 + pulse * 2; });
          forkTips.forEach((f) => { (f.material as THREE.MeshStandardMaterial).emissiveIntensity = 2 + pulse * 8; });
          for (const a of arcs) { a.visible = pulse > 0.15 && Math.random() < 0.55; if (a.visible) a.scale.set(1, 0.8 + Math.random() * 0.5, 1); }
        });
        break;
      }

      // ------------------------------------------------------------ ROCKET LAUNCHER
      case 'rocket': {
        // tube épais + bandes oranges
        add(new THREE.CylinderGeometry(0.082, 0.088, 0.76, 16), M.hull, 0, 0.02, -0.36, Math.PI / 2);
        add(new THREE.CylinderGeometry(0.093, 0.093, 0.045, 16), M.orange, 0, 0.02, -0.66, Math.PI / 2);
        add(new THREE.CylinderGeometry(0.093, 0.093, 0.035, 16), M.orange, 0, 0.02, -0.30, Math.PI / 2);
        add(new THREE.CylinderGeometry(0.09, 0.09, 0.03, 16), M.carbon, 0, 0.02, -0.10, Math.PI / 2);
        add(new THREE.CylinderGeometry(0.07, 0.07, 0.02, 16), this.glowMat(0xff5a12, 2.2), 0, 0.02, -0.735, Math.PI / 2);
        // tambour rotatif — 5 têtes de roquettes visibles
        const drum = new THREE.Group();
        drum.position.set(0, 0.02, -0.02);
        const drumBody = new THREE.Mesh(new THREE.CylinderGeometry(0.105, 0.105, 0.19, 20), M.brushed);
        drumBody.rotation.x = Math.PI / 2;
        drum.add(drumBody);
        for (let i = 0; i < 5; i++) {
          const a = (i / 5) * Math.PI * 2;
          const cx = Math.cos(a) * 0.062, cy = Math.sin(a) * 0.062;
          const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.2, 10), M.carbon);
          tube.rotation.x = Math.PI / 2; tube.position.set(cx, cy, 0);
          const head = new THREE.Mesh(new THREE.ConeGeometry(0.026, 0.06, 10), M.orange);
          head.rotation.x = -Math.PI / 2; head.position.set(cx, cy, -0.125);
          const led = new THREE.Mesh(new THREE.SphereGeometry(0.008, 6, 5), this.glowMat(0xff3300, 3));
          led.position.set(cx, cy, -0.16);
          drum.add(tube, head, led);
        }
        g.add(drum);
        // buse d'échappement arrière + lueur interne
        add(new THREE.CylinderGeometry(0.062, 0.082, 0.2, 14), M.brushed, 0, 0.02, 0.26, Math.PI / 2);
        const inner = add(new THREE.CylinderGeometry(0.055, 0.055, 0.06, 14), this.glowMat(0xff4400, 2.5), 0, 0.02, 0.33, Math.PI / 2);
        // ailerons du tube + poignée avant
        for (const s of [-1, 1]) {
          add(new THREE.BoxGeometry(0.012, 0.075, 0.28), M.brushed, s * 0.085, 0.055, -0.42, 0, 0, s * 0.3);
        }
        add(new THREE.BoxGeometry(0.05, 0.10, 0.06), M.rubber, 0, -0.10, -0.34, -0.18);
        // crosse + grip
        add(new THREE.BoxGeometry(0.055, 0.115, 0.22), M.carbon, 0, -0.02, 0.36);
        add(new THREE.BoxGeometry(0.07, 0.09, 0.05), M.rubber, 0, -0.055, 0.46);
        add(new THREE.BoxGeometry(0.062, 0.20, 0.085), M.rubber, 0, -0.155, 0.16, 0.24);
        add(new THREE.TorusGeometry(0.045, 0.008, 6, 12, Math.PI), M.brushed, 0, -0.085, 0.08, 0, Math.PI / 2, Math.PI);
        // visée / guidon
        add(new THREE.BoxGeometry(0.016, 0.06, 0.016), M.brushed, 0, 0.13, -0.55);
        add(new THREE.BoxGeometry(0.03, 0.012, 0.012), this.glowMat(0xff7a00, 3), 0, 0.155, -0.55);
        add(new THREE.BoxGeometry(0.05, 0.055, 0.02), M.carbon, 0, 0.135, 0.06);
        const sc = this.makeScreen('RL', '#ff9a3c');
        sc.mesh.position.set(-0.098, 0.02, -0.02); sc.mesh.rotation.y = -Math.PI / 2;
        g.add(sc.mesh); screen = sc.screen;

        muzzle.position.set(0, 0.02, -0.80);
        gripAt.set(0, -0.155, 0.16);
        foreAt.set(0, -0.105, -0.34);

        let drumTarget = 0, drumCur = 0;
        fire.push(() => { drumTarget += (Math.PI * 2) / 5; });
        anim.push((t, dt) => {
          drumCur += (drumTarget - drumCur) * Math.min(1, dt * 12);
          drum.rotation.z = drumCur;
          (inner.material as THREE.MeshStandardMaterial).emissiveIntensity = 1.6 + Math.sin(t * 6) * 0.7;
        });
        break;
      }

      // ------------------------------------------------------------ RAILGUN
      case 'rail': {
        // barillet de rails parallèles
        add(new THREE.BoxGeometry(0.06, 0.075, 0.62), M.hull, 0, 0.005, -0.28);
        for (const s of [-1, 1]) {
          add(new THREE.BoxGeometry(0.014, 0.03, 1.0), M.steel, s * 0.033, 0.02, -0.50);
          add(new THREE.CylinderGeometry(0.008, 0.008, 0.9, 6), M.brushed, s * 0.05, -0.02, -0.46, Math.PI / 2); // câbles de refroidissement
        }
        add(new THREE.CylinderGeometry(0.019, 0.022, 1.05, 12), M.steel, 0, 0.02, -0.55, Math.PI / 2);
        // 4 ailettes de dissipation
        for (let i = 0; i < 4; i++) add(new THREE.BoxGeometry(0.085, 0.055, 0.012), M.brushed, 0, 0.045, -0.10 - i * 0.06);
        // anneaux émissifs (cascade de charge)
        const rings: THREE.Mesh[] = [];
        for (let i = 0; i < 6; i++) rings.push(add(new THREE.TorusGeometry(0.042 + i * 0.002, 0.0085, 8, 22), this.glowMat(0x2fff9a, 0.1), 0, 0.02, -0.30 - i * 0.13));
        // lunette + lentille
        add(new THREE.CylinderGeometry(0.031, 0.031, 0.26, 14), M.carbon, 0, 0.115, -0.10, Math.PI / 2);
        add(new THREE.CylinderGeometry(0.034, 0.034, 0.02, 14), M.brushed, 0, 0.115, -0.23, Math.PI / 2);
        const lens = add(new THREE.CircleGeometry(0.028, 16), this.glowMat(0x2fff9a, 1.6), 0, 0.115, -0.242);
        add(new THREE.BoxGeometry(0.018, 0.05, 0.018), M.brushed, 0, 0.075, -0.02);
        add(new THREE.BoxGeometry(0.018, 0.05, 0.018), M.brushed, 0, 0.075, -0.19);
        // crosse squelette + grip
        add(new THREE.BoxGeometry(0.05, 0.10, 0.20), M.carbon, 0, -0.01, 0.28);
        add(new THREE.BoxGeometry(0.06, 0.08, 0.04), M.rubber, 0, -0.045, 0.39);
        add(new THREE.BoxGeometry(0.02, 0.09, 0.14), M.carbon, 0, 0.055, 0.32);
        add(new THREE.BoxGeometry(0.06, 0.20, 0.085), M.rubber, 0, -0.155, 0.13, 0.26);
        add(new THREE.TorusGeometry(0.043, 0.008, 6, 12, Math.PI), M.brushed, 0, -0.08, 0.055, 0, Math.PI / 2, Math.PI);
        const sc = this.makeScreen('RG', '#2fff9a');
        sc.mesh.position.set(-0.033, 0.02, 0.06); sc.mesh.rotation.y = -Math.PI / 2;
        g.add(sc.mesh); screen = sc.screen;

        muzzle.position.set(0, 0.02, -1.06);
        gripAt.set(0, -0.155, 0.13);
        foreAt.set(0, -0.06, -0.36);

        let charge = 1;
        fire.push(() => { charge = 0; });
        anim.push((_t, dt) => {
          charge = Math.min(1, charge + dt / WEAPONS.rail.fireInterval);
          rings.forEach((r, i) => {
            const on = THREE.MathUtils.clamp(charge * rings.length - i, 0, 1);
            (r.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.08 + on * 3.4;
          });
          (lens.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.4 + charge * 2.2;
        });
        break;
      }

      // ------------------------------------------------------------ SHOTGUN
      case 'shotgun': {
        // deux canons parallèles
        for (const s of [-1, 1]) {
          add(new THREE.CylinderGeometry(0.032, 0.034, 0.78, 14), M.steel, s * 0.037, 0.03, -0.40, Math.PI / 2);
          add(new THREE.TorusGeometry(0.036, 0.005, 6, 14), M.brushed, s * 0.037, 0.03, -0.74, 0, Math.PI / 2);
        }
        add(new THREE.BoxGeometry(0.086, 0.02, 0.72), M.carbon, 0, 0.03, -0.40); // pontet entre canons
        // boîte de culasse
        add(new THREE.BoxGeometry(0.10, 0.13, 0.30), M.hull, 0, -0.005, 0.05);
        add(new THREE.BoxGeometry(0.105, 0.035, 0.20), M.orange, 0, 0.055, 0.03);
        // pompe coulissante avec coquilles brass
        const pump = new THREE.Group();
        pump.position.set(0, -0.03, -0.30);
        const pumpBody = new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.075, 0.22), M.wood);
        const pumpRail = new THREE.Mesh(new THREE.BoxGeometry(0.115, 0.02, 0.23), M.carbon);
        pumpRail.position.y = 0.045;
        pump.add(pumpBody, pumpRail);
        for (let i = 0; i < 3; i++) {
          const sh = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, 0.055, 10), M.brass);
          sh.rotation.x = Math.PI / 2;
          sh.position.set(-0.03 + i * 0.03, -0.045, 0.02);
          const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.012, 10), M.glassRed);
          cap.rotation.x = Math.PI / 2; cap.position.set(-0.03 + i * 0.03, -0.045, -0.03);
          pump.add(sh, cap);
        }
        g.add(pump);
        // fût carbone + crosse bois
        add(new THREE.BoxGeometry(0.09, 0.05, 0.26), M.carbon, 0, -0.055, -0.55);
        add(new THREE.BoxGeometry(0.062, 0.115, 0.26), M.wood, 0, -0.045, 0.30, -0.16);
        add(new THREE.BoxGeometry(0.07, 0.10, 0.035), M.rubber, 0, -0.085, 0.44, -0.16);
        add(new THREE.BoxGeometry(0.058, 0.19, 0.08), M.rubber, 0, -0.15, 0.14, 0.24);
        add(new THREE.TorusGeometry(0.042, 0.008, 6, 12, Math.PI), M.brushed, 0, -0.075, 0.06, 0, Math.PI / 2, Math.PI);
        add(new THREE.BoxGeometry(0.012, 0.034, 0.012), M.steel, 0, -0.078, 0.06, 0.2);
        // bead sight
        add(new THREE.SphereGeometry(0.011, 8, 6), this.glowMat(0xffe08a, 3), 0, 0.068, -0.76);
        add(new THREE.BoxGeometry(0.014, 0.026, 0.014), M.brushed, 0, 0.052, -0.76);
        const sc = this.makeScreen('SG', '#ffe08a');
        sc.mesh.position.set(-0.052, 0.0, 0.06); sc.mesh.rotation.y = -Math.PI / 2;
        g.add(sc.mesh); screen = sc.screen;

        muzzle.position.set(0, 0.03, -0.80);
        gripAt.set(0, -0.15, 0.14);
        foreAt.set(0, -0.075, -0.30);

        let cycle = 0;
        fire.push(() => { cycle = 1; });
        anim.push((_t, dt) => {
          if (cycle > 0) {
            cycle = Math.max(0, cycle - dt * 3.2);
            // aller-retour : recule vite (cycle 1→0.5), revient (0.5→0)
            const s = cycle > 0.5 ? (1 - cycle) * 2 : cycle * 2;
            pump.position.z = -0.30 + s * 0.13;
          } else pump.position.z = -0.30;
        });
        break;
      }
    }

    // Mains gantées : droite au grip, gauche au garde-main / à la pompe
    const right = this.makeHand(1, glove, band);
    right.position.copy(gripAt).add(new THREE.Vector3(0.012, 0.075, -0.01));
    right.rotation.set(0.35, 0, -0.12);
    const left = this.makeHand(-1, glove, band);
    left.position.copy(foreAt).add(new THREE.Vector3(-0.012, 0.075, 0.0));
    left.rotation.set(0.2, 0, 0.16);
    g.add(right, left);

    g.add(muzzle);
    const rig: WeaponRig = { group: g, muzzle, anim, fire, screen, ammo: WEAPONS[id].ammoStart, accentMats };
    if (screen) this.drawScreen(screen, rig.ammo);
    return rig;
  }

  // ---------------------------------------------------------------- API publique

  select(id: WeaponId): void {
    if (this.current === id) return;
    this.current = id;
    this.switchT = 0.22;
    this.rigs.forEach((w, k) => { w.group.visible = k === id; });
  }

  /** Recul : accepte un WeaponId (préréglage) ou une amplitude brute. */
  kick(kind: WeaponId | number = this.current): void {
    const k = typeof kind === 'number'
      ? { z: kind * 0.6, pitch: kind * 0.8, yaw: kind * 0.06, roll: kind * 0.1, punch: kind * 0.3 }
      : KICKS[kind];
    this.kickZ = Math.min(0.14, this.kickZ + k.z);
    this.kickPitch = Math.min(0.20, this.kickPitch + k.pitch);
    this.kickYaw += (Math.random() - 0.5) * 2 * k.yaw;
    this.kickRoll += (Math.random() - 0.5) * 2 * k.roll;
    this.punchP += k.punch;
    this.punchY += (Math.random() - 0.5) * k.punch * 0.5;
    // seul un recul « d'arme » anime le mécanisme (pompe, tambour, bobines) ;
    // les secousses brutes (pad, dégâts) ne déclenchent rien.
    if (typeof kind !== 'number') for (const f of this.rigs.get(kind)!.fire) f();
  }

  /** Inertie de visée : deltas souris/joystick accumulés. */
  addLook(dx: number, dy: number): void {
    this.lookAX += dx; this.lookAY += dy;
  }

  /**
   * Delta de punch caméra à appliquer cette frame (pitch/yaw en radians).
   * Le punch monte au tir puis redescend : la somme des deltas revient à zéro.
   */
  consumePunch(): { pitch: number; yaw: number } {
    const pitch = this.punchP - this.emitP;
    const yaw = this.punchY - this.emitY;
    this.emitP = this.punchP; this.emitY = this.punchY;
    return { pitch, yaw };
  }

  /** Rafraîchit l'écran de munitions de l'arme donnée. */
  setAmmo(id: WeaponId, count: number): void {
    const w = this.rigs.get(id);
    if (!w || !w.screen || w.ammo === count) return;
    w.ammo = count;
    this.drawScreen(w.screen, count);
  }

  /** Couleur d'accent (légende) appliquée aux bandes des gants. */
  setAccent(color: number): void {
    this.accent = color;
    this.rigs.forEach((w) => {
      for (const m of w.accentMats) { m.color.setHex(color); m.emissive.setHex(color); }
    });
  }

  muzzleWorld(out: THREE.Vector3): THREE.Vector3 {
    const m = this.rigs.get(this.current)!.muzzle;
    m.updateWorldMatrix(true, false);
    return out.setFromMatrixPosition(m.matrixWorld);
  }

  update(dt: number, speed: number, onGround: boolean, visible: boolean, lookDX = 0, lookDY = 0): void {
    this.rig.visible = visible;
    const t = performance.now() / 1000;
    this.idleT += dt;
    this.addLook(lookDX, lookDY);

    // Bob de marche + dip d'atterrissage
    if (onGround && speed > 1) this.bobT += dt * Math.min(15, 4.5 + speed * 0.95);
    if (onGround && !this.wasGround) this.landDip = 0.075;
    this.wasGround = onGround;
    this.landDip = Math.max(0, this.landDip - dt * 0.4);
    this.switchT = Math.max(0, this.switchT - dt);

    // Ressorts de recul (retour exponentiel)
    const decay = Math.exp(-dt * 11);
    this.kickZ *= decay; this.kickPitch *= decay;
    this.kickYaw *= Math.exp(-dt * 9); this.kickRoll *= Math.exp(-dt * 9);
    this.punchP *= Math.exp(-dt * 7); this.punchY *= Math.exp(-dt * 7);

    // Sway : l'arme traîne derrière la visée puis revient (ressort amorti)
    const targetX = THREE.MathUtils.clamp(-this.lookAX * 0.00035, -0.05, 0.05);
    const targetY = THREE.MathUtils.clamp(this.lookAY * 0.00035, -0.045, 0.045);
    this.lookAX *= Math.exp(-dt * 12); this.lookAY *= Math.exp(-dt * 12);
    this.swayX += (targetX - this.swayX) * Math.min(1, dt * 9);
    this.swayY += (targetY - this.swayY) * Math.min(1, dt * 9);

    const amp = Math.min(1, speed / 6);
    const bobX = Math.sin(this.bobT) * 0.014 * amp;
    const bobY = Math.abs(Math.cos(this.bobT)) * 0.017 * amp;
    const idleX = Math.sin(this.idleT * 1.25) * 0.0035;
    const idleY = Math.cos(this.idleT * 0.85) * 0.0028;

    this.rig.position.set(
      this.base.x + bobX + idleX + this.swayX,
      this.base.y + bobY + idleY - this.landDip - this.switchT * 0.9 + this.swayY,
      this.base.z + this.kickZ,
    );
    this.rig.rotation.set(
      -this.kickPitch + this.switchT * 1.1 + this.swayY * 1.4 - this.landDip * 0.6,
      this.kickYaw + Math.sin(this.bobT * 0.5) * 0.012 * amp - this.swayX * 1.2,
      this.kickRoll + bobX * 0.7 + this.swayX * 1.6,
    );

    for (const a of this.rigs.get(this.current)!.anim) a(t, dt);
    // le railgun recharge même quand il n'est pas en main (cohérence visuelle)
    const rail = this.rigs.get('rail')!;
    if (this.current !== 'rail') for (const a of rail.anim) a(t, dt);
  }
}
